import React, { useMemo } from 'react';
import { Loader2, Check, ExternalLink } from 'lucide-react';
import CreativePreview from './CreativePreview';
import CriteriaCheckmarkRow from './CriteriaCheckmarkRow';
import ItemValueChangeRow from './ItemValueChangeRow';
import { getIntentTone } from './intentTone';
import { formatByHint } from './criteriaBarUtils';

const METRIC_LABEL = {
  spend: 'Gasto',
  conversions: 'Conv.',
  cpa: 'CPA',
  roas: 'ROAS',
  inline_link_click_ctr: 'CTR',
  ctr: 'CTR',
  frequency: 'Freq.',
  impressions: 'Impr.',
  daily_budget: 'Budget',
};

const metricHint = (k) => {
  if (k === 'spend' || k === 'cpa' || k === 'daily_budget') return 'currency';
  if (k === 'conversions' || k === 'impressions') return 'integer';
  if (k === 'roas') return 'ratio';
  if (k === 'ctr' || k === 'inline_link_click_ctr') return 'percentage_2d';
  return 'decimal_2';
};

export default function ItemCard({
  item,
  suggestionCriteria,
  intent,
  currency,
  onApply,
  isApplying,
  isApplied,
  suggestionBreadcrumb,
  suggestionMetricKeysToShow,
  // When selectable, the top-right action slot shows an include/exclude checkbox
  // (the card footer's "Aplicar (N)" is the action) instead of a per-row Aplicar button.
  selectable = false,
  selected = false,
  onToggleSelect,
  // For applicable:false (deep-link) cards, each row links out to the entity on its platform.
  deepLinkUrl = null,
  deepLinkLabel = 'Ver',
}) {
  const tone = getIntentTone(intent);

  const itemBreadcrumb = useMemo(() => {
    const b = item?.breadcrumb;
    if (Array.isArray(b)) return b;
    if (Array.isArray(suggestionBreadcrumb)) return suggestionBreadcrumb;
    return [];
  }, [item, suggestionBreadcrumb]);

  const metrics = (item?.metrics_extended && typeof item.metrics_extended === 'object')
    ? item.metrics_extended
    : ((item?.metrics && typeof item.metrics === 'object') ? item.metrics : {});

  // §6.14: prefer item-level metric_keys_to_show, then suggestion-level, then local fallback.
  const metricKeys = useMemo(() => {
    if (Array.isArray(item?.metric_keys_to_show)) return item.metric_keys_to_show.slice(0, 8);
    if (Array.isArray(suggestionMetricKeysToShow)) return suggestionMetricKeysToShow;
    const base = ['spend', 'conversions', 'cpa', 'roas'];
    const fromCriteria = Array.isArray(suggestionCriteria)
      ? suggestionCriteria.map((c) => c?.metric_key).filter(Boolean)
      : [];
    const extra = fromCriteria.filter((k) => !base.includes(k));
    return [...base, ...extra];
  }, [item, suggestionMetricKeysToShow, suggestionCriteria]);

  // Skip footer metrics that already render as a criteria chip above (same metric_key) —
  // e.g. adset_spend_share on "Iterar anuncio ganador" — so the footer doesn't duplicate
  // the row. Keys not covered by a criterion (extra context metrics) still show.
  const criteriaMetricKeys = useMemo(
    () => new Set(
      (Array.isArray(suggestionCriteria) ? suggestionCriteria : [])
        .map((c) => c?.metric_key)
        .filter(Boolean)
    ),
    [suggestionCriteria]
  );

  const footerMetrics = metricKeys
    .map((k) => {
      if (criteriaMetricKeys.has(k)) return null; // already shown as a criteria chip above
      const v = metrics?.[k];
      if (v == null) return null;
      return {
        k,
        label: METRIC_LABEL[k] || k,
        value: formatByHint(v, metricHint(k), currency),
      };
    })
    .filter(Boolean);

  return (
    <div
      className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3"
      style={isApplied ? { opacity: 0.55, pointerEvents: 'none' } : undefined}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <CreativePreview
            size="md"
            entity={item?.target_entity || null}
            subtitle={itemBreadcrumb}
          />
        </div>
        {selectable ? (
          isApplied ? (
            <span
              className="shrink-0 inline-flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-2 text-xs font-semibold whitespace-nowrap"
              style={{ background: tone.pillBg, color: tone.surface }}
            >
              <Check className="w-3.5 h-3.5" aria-hidden /> Aplicado
            </span>
          ) : (
            <input
              type="checkbox"
              checked={Boolean(selected)}
              onChange={() => onToggleSelect?.(item)}
              aria-label={`Incluir ${item?.target_entity?.name || 'anuncio'} en la optimización`}
              className="shrink-0 mt-1 h-5 w-5 accent-emerald-500 cursor-pointer"
            />
          )
        ) : deepLinkUrl ? (
          <a
            href={deepLinkUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${deepLinkLabel}: ${item?.target_entity?.name || 'anuncio'}`}
            className="shrink-0 inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold border border-white/10 whitespace-nowrap hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            style={{ background: tone.pillBg, color: tone.surface }}
          >
            {deepLinkLabel}
            <ExternalLink className="w-3.5 h-3.5" aria-hidden />
          </a>
        ) : onApply ? (
          <button
            type="button"
            onClick={() => onApply?.(item)}
            disabled={Boolean(isApplying) || Boolean(isApplied)}
            className="shrink-0 rounded-lg px-3 py-2 text-xs font-semibold border border-white/10 whitespace-nowrap"
            style={{ background: tone.pillBg, color: tone.surface }}
          >
            {isApplied ? (
              <span className="inline-flex items-center gap-1.5">
                <Check className="w-3.5 h-3.5" aria-hidden /> Aplicado
              </span>
            ) : isApplying ? (
              <span className="inline-flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden /> Aplicando…
              </span>
            ) : (
              'Aplicar'
            )}
          </button>
        ) : null}
      </div>

      {Array.isArray(suggestionCriteria) && suggestionCriteria.length ? (
        <div className="mb-2 flex flex-wrap gap-2">
          {suggestionCriteria.map((c, cIdx) => {
            const evalRow = Array.isArray(item?.criteria_evaluation) ? item.criteria_evaluation[cIdx] : null;
            return (
              <CriteriaCheckmarkRow
                key={`${item?.id ?? 'item'}-${c?.metric_key ?? cIdx}`}
                label={c?.label}
                actualValue={evalRow?.actual_value ?? c?.actual_value}
                thresholdValue={evalRow?.threshold_value ?? c?.threshold_value}
                formatHint={c?.format_hint}
                currency={currency}
                passes={evalRow?.passes_criterion ?? true}
              />
            );
          })}
        </div>
      ) : null}

      {item?.value_change ? (
        <div className="mb-2">
          <ItemValueChangeRow valueChange={item.value_change} intent={intent} />
        </div>
      ) : null}

      {footerMetrics.length ? (
        <div className="pt-2 border-t border-white/10 flex flex-wrap gap-4 text-[11px]">
          {footerMetrics.map((m) => (
            <span key={m.k} className="text-white/70">
              {m.label}{' '}
              <span className="text-white font-medium tabular-nums">
                {m.value}
              </span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

